import React, { forwardRef, ReactElement, useImperativeHandle, useState } from 'react';
import { Modal } from 'react-native';
import Overlay from '../../common/Overlay';
import PhonePopup from './profilepopups/PhonePopup';
import ProfileMemberPopup from './profilepopups/ProfileMemberPopup';
import { ProfileMemberItemProps } from './ProfileMemberItem';
import NamePopup from './profilepopups/NamePopup';

export interface ProfileManagerHandles {
  showNamePopup: () => void;
  showPhoneNumberPopup: () => void;
  showProfileMemberPopup: (profileProps: ProfileMemberItemProps, callback?, options?: { suspendDrawer?: boolean }) => void;
  verifyUserCanAcceptMission: (userPrefs, callback, options?: { suspendDrawer?: boolean }) => boolean;
  verifyUserCanCashOut: (userPrefs, callback) => boolean;
}

const ProfileManager = forwardRef((props, ref) => {
  const [profileModal, setProfileModal] = useState<ReactElement>();
  const [visible, setVisible] = useState(false);
  const [profileProps, setProfileProps] = useState<ProfileMemberItemProps>();

  const handleOnClose = () => {
    if (!profileProps?.hideCloseButton) {
      setVisible(false);
    }
  };

  useImperativeHandle(ref, (): ProfileManagerHandles => {
    return {
      showNamePopup() {
        setProfileProps(undefined);
        setProfileModal(<NamePopup onClose={handleOnClose} />);
        setVisible(true);
      },
      showPhoneNumberPopup() {
        setProfileProps(undefined);
        setProfileModal(<PhonePopup onClose={handleOnClose} />);
        setVisible(true);
      },
      showProfileMemberPopup(profileProps: ProfileMemberItemProps, callback?, options?: { suspendDrawer?: boolean }) {
        setProfileProps(profileProps);
        setProfileModal(
          <ProfileMemberPopup
            profileProps={profileProps}
            suspendDrawer={options?.suspendDrawer}
            onClose={handleOnClose}
            onConfirm={() => {
              callback && callback();
            }}
          />,
        );
        setVisible(true);
      },
      verifyUserCanAcceptMission(userPrefs, callback, options?: { suspendDrawer?: boolean }) {
        let requiredProfileMembersMaxPage = 0;

        if (userPrefs.firstName === '' || userPrefs.firstName === undefined || userPrefs.lastName === '' || userPrefs.lastName === undefined) {
          requiredProfileMembersMaxPage++;
        }

        if (userPrefs.phoneNumber === undefined || userPrefs.phoneNumber === '') {
          requiredProfileMembersMaxPage++;
        }

        if (userPrefs.profiles?.find((x) => x.key === 'BirthDate') === undefined) {
          requiredProfileMembersMaxPage++;
        }

        if (userPrefs.profiles?.find((x) => x.key === 'Gender') === undefined) {
          requiredProfileMembersMaxPage++;
        }

        if (userPrefs.profiles?.find((x) => x.key === 'EducationLevel') === undefined) {
          requiredProfileMembersMaxPage++;
        }

        if (requiredProfileMembersMaxPage == 0) {
          return true;
        }

        fillBasicProfileMembers(userPrefs, 1, requiredProfileMembersMaxPage, callback, options);

        return false;
      },
      verifyUserCanCashOut(userPrefs, callback) {
        if (userPrefs.firstName === '' || userPrefs.firstName === undefined || userPrefs.lastName === '' || userPrefs.lastName === undefined) {
          setProfileModal(
            <NamePopup
              onClose={handleOnClose}
              onConfirm={(userPrefs) => {
                callback && callback(userPrefs);
              }}
            />,
          );
          setVisible(true);

          return false;
        }
        return true;
      },
    };
  }, []);

  const fillBasicProfileMembers = (userPrefs, currentPage, maxPage, callback, options?: { suspendDrawer?: boolean }) => {
    if (userPrefs.firstName === '' || userPrefs.firstName === undefined || userPrefs.lastName === '' || userPrefs.lastName === undefined) {
      setProfileModal(
        <NamePopup
          suspendDrawer={options?.suspendDrawer}
          height={370}
          currentPage={currentPage}
          maxPage={maxPage}
          onClose={handleOnClose}
          onConfirm={(userPrefs) => {
            fillBasicProfileMembers(userPrefs, currentPage + 1, maxPage, callback, options);
          }}
        />,
      );
      setVisible(true);
    } else if (userPrefs.phoneNumber === undefined || userPrefs.phoneNumber === '') {
      setProfileModal(
        <PhonePopup
          suspendDrawer={options?.suspendDrawer}
          height={430}
          currentPage={currentPage}
          maxPage={maxPage}
          onClose={handleOnClose}
          onConfirm={(userPrefs) => {
            fillBasicProfileMembers(userPrefs, currentPage + 1, maxPage, callback, options);
          }}
        />,
      );
      setVisible(true);
    } else {
      let profileMember = undefined;

      if (!userPrefs.profiles?.find((x) => x.key === 'BirthDate')) {
        profileMember = userPrefs.profileMembers.find((m) => m.key === 'BirthDate');
      } else if (!userPrefs.profiles?.find((x) => x.key === 'Gender')) {
        profileMember = userPrefs.profileMembers.find((m) => m.key === 'Gender');
      } else if (!userPrefs.profiles?.find((x) => x.key === 'EducationLevel')) {
        profileMember = userPrefs.profileMembers.find((m) => m.key === 'EducationLevel');
      }

      if (profileMember) {
        let newProps: ProfileMemberItemProps = {
          profileMember: profileMember,
          value: '',
        };

        setProfileModal(
          <ProfileMemberPopup
            profileProps={newProps}
            suspendDrawer={options?.suspendDrawer}
            height={370}
            currentPage={currentPage}
            maxPage={maxPage}
            onClose={handleOnClose}
            onConfirm={(userPrefs) => {
              setTimeout(() => {
                fillBasicProfileMembers(userPrefs, currentPage + 1, maxPage, callback, options);
              }, 300);
            }}
          />,
        );

        setVisible(true);
      } else {
        callback && callback(userPrefs);
      }
    }
  };

  return (
    <Modal animationType="none" visible={visible} transparent={true}>
      <Overlay showOverLay={true} zIndex={1998} onPress={handleOnClose}>
        {profileModal}
      </Overlay>
    </Modal>
  );
});

export default ProfileManager;
